// front-end/src/components/block-system/block-manager/block-renderer.js

import {getBlockCreator} from '../block-configs';
import {loadContentForNewElements} from '../../content-editor-src';
import {getAuthToken, isAuthenticated} from '../../auth';
import {reinitializeCarousel} from '../../carousel';
import {updateBlockCounter} from './block-state';
import {addDeleteButton} from './block-editor';
import {makeBlockDraggable} from './block-sortable';
import {apiGet} from '../../../utils/api';
import {config} from '../../../config/app-config';

// НОВОЕ: Проверяем, должны ли показываться статусы
function shouldShowNewsStatusControls() {
    return window.location.pathname.match(/news-feed(\.html)?$/);
}

function shouldShowCaseStatusControls() {
    return window.location.pathname.match(/cases(\.html)?$/) && isAuthenticated();
}

function shouldShowCourseStatusControls() {
    return window.location.pathname.match(/courses(\.html)?$/);
}


export function createAndTrackBlock(block, sectionElement) {
    // Используем id элемента, если есть, иначе используем класс
    const sectionId = sectionElement.id || sectionElement.getAttribute('class');
    const {class_name: blockType, data_block_name: blockName, order} = block;
    const createBlock = getBlockCreator(sectionId, blockType);


    if (!createBlock) {
        console.warn(`[BlockSystem] Block creator not found: ${blockType} for section ${sectionId}`);
        return null;
    }

    const blockElement = createBlock(block, sectionElement);

    if (order !== undefined && order !== null) {
        blockElement.setAttribute('data-order', String(order));
    } else {
        blockElement.setAttribute('data-order', '0');
    }

    // Встановлюємо правильний page context для блоків всередині news/diagnostic секцій
    const isPagedSection = sectionId === 'news-content' || sectionElement.id === 'news-content' ||
                           sectionId === 'diagnostic-content' || sectionElement.id === 'diagnostic-content';
    if (isPagedSection) {
        const pageContext = determinePageContext();
        if (pageContext && pageContext !== 'Home') {
            const contentElements = blockElement.querySelectorAll('[data-content]');
            contentElements.forEach(el => {
                el.setAttribute('data-page', pageContext);
            });
        }
    }

    if (blockName && blockName.startsWith('course-card-') && block.id) {
        console.log(`Detected course block: ${blockName}, loading course attributes...`);
        loadCourseAttributes(blockElement, block.id, blockName);
    }

    // НОВАЯ ЛОГИКА: Проверяем, является ли блок новостным
    if (blockName && blockName.startsWith('news-block') && block.id) {
        console.log(`Detected news block: ${blockName}, loading news attributes...`);
        loadNewsAttributes(blockElement, block.id, blockName);
    }

    // НОВАЯ ЛОГИКА: Проверяем, является ли блок кейсом
    if (blockName && blockName.startsWith('case-card-') && block.id) {
        console.log(`Detected case block: ${blockName}, loading case attributes...`);
        loadCaseAttributes(blockElement, block.id, blockName);
    }

    if (isAuthenticated()) {
        if (block.id) {
            addDeleteButton(blockElement, block.id);
        } else {
            addDeleteButton(blockElement);
        }

        blockElement.setAttribute('draggable', 'true');
        makeBlockDraggable(blockElement);
    }

    const match = blockName?.match(/^(.+)-(\d+)$/);
    if (match && match[1] === blockType) {
        const blockNumber = parseInt(match[2], 10);
        updateBlockCounter(blockType, blockNumber);
    }

    return blockElement;
}


async function loadCourseAttributes(blockElement, blockId, blockName) {
    try {
        console.log(`Loading course attributes for block: ${blockName} (ID: ${blockId})`);

        // Делаем запрос к API для получения курса по block_id
        const courseData = await apiGet(`${config.api.paths.coursesById}/${blockId}`, {auth: false});
        if (courseData) {
            console.log(`Course data found for block ${blockName}:`, courseData);

            // Устанавливаем атрибуты курса на блок
            blockElement.setAttribute('data-course-id', courseData.id);
            blockElement.setAttribute('data-course-status', courseData.status || 'draft');
            blockElement.setAttribute('data-course-slug', courseData.slug);

            // Обновляем ссылку если курс опубликован
            if (courseData.status === 'published' || blockId) {
                const courseLink = blockElement.querySelector('a.news-link-to-feed-container');
                if (courseLink) {
                    const dynamicUrl = `/course/${courseData.slug}`;
                    courseLink.href = dynamicUrl;
                    courseLink.setAttribute('data-page', courseData.slug);
                    courseLink.classList.remove('inactive');
                    courseLink.classList.add('active');

                    console.log(`Course link activated for ${blockName}: ${dynamicUrl}`);
                }
            }

            // Обновляем индикатор статуса только на courses.html
            if (shouldShowCourseStatusControls()) {
                updateCourseStatusIndicator(blockElement, courseData.status || 'draft');
            }

            // Добавляем контролы статуса только на courses.html
            if (isAuthenticated() && shouldShowCourseStatusControls()) {
                setTimeout(() => {
                    addCourseStatusControls(blockElement, courseData.id, courseData.status || 'draft');
                }, 100);
            }

        } else {
            console.log(`No course data found for block ${blockName}`);
        }

    } catch (error) {
        console.warn(`Failed to load course attributes for block ${blockName}:`, error);
    }
}

function updateCourseStatusIndicator(blockElement, status) {
    // ФИКС: Проверяем авторизацию перед показом индикатора
    if (!getAuthToken()) {
        // Для неавторизованных удаляем индикатор
        const existingIndicator = blockElement.querySelector('.course-status-indicator');
        if (existingIndicator) {
            existingIndicator.remove();
        }
        return;
    }

    // НОВОЕ: Проверяем, должны ли показываться индикаторы
    if (!shouldShowCourseStatusControls()) {
        return;
    }

    let indicator = blockElement.querySelector('.course-status-indicator');

    if (!indicator) {
        indicator = document.createElement('div');
        indicator.className = 'course-status-indicator';
        blockElement.appendChild(indicator);
    }

    indicator.className = `course-status-indicator status-${status}`;
    indicator.innerHTML = `<span class="status-text">${status.toUpperCase()}</span>`;
}

// НОВАЯ ФУНКЦИЯ: Добавление контролов статуса для курсов
function addCourseStatusControls(blockElement, courseId, currentStatus) {
    // Удаляем существующие контролы если есть
    const existingControls = blockElement.querySelector('.course-status-controls');
    if (existingControls) {
        existingControls.remove();
    }

    const controlsContainer = document.createElement('div');
    controlsContainer.className = 'course-status-controls';
    controlsContainer.innerHTML = `
    <div class="status-controls-inner">
      <span class="current-status">Status: ${currentStatus.toUpperCase()}</span>
      <button class="toggle-status-btn" data-course-id="${courseId}" data-current-status="${currentStatus}">
        ${currentStatus === 'draft' ? 'Publish' : 'Unpublish'}
      </button>
    </div>
  `;

    blockElement.appendChild(controlsContainer);

    const toggleBtn = controlsContainer.querySelector('.toggle-status-btn');
    toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (window.courseManager) {
            console.log('Calling toggleCourseStatus with:', {courseId, currentStatus});
            window.courseManager.toggleCourseStatus(blockElement, courseId, currentStatus);
        } else {
            console.error('CourseManager not found in window.courseManager');
        }
    });

    console.log(`Course status controls added for course ID: ${courseId}, status: ${currentStatus}`);
}

function determinePageContext() {
    // 1. Сторінка діагностики
    const diagPathMatch = window.location.pathname.match(/^\/diagnostic\/([^/]+)/);
    if (diagPathMatch) return `Diagnostic - ${diagPathMatch[1]}`;
    if (window.location.pathname.match(/diagnostic-detail(\.html)?$/)) {
        const diagnosticSlug = new URLSearchParams(window.location.search).get('slug');
        if (diagnosticSlug) return `Diagnostic - ${diagnosticSlug}`;
    }

    // 2. Проверяем URL для новостных страниц
    if (window.location.pathname.match(/\/News(\.html)?$/)) {
        const urlParams = new URLSearchParams(window.location.search);
        const newsSlug = urlParams.get('slug');
        if (newsSlug) {
            console.log("Page context from URL slug:", newsSlug);
            // ВАЖНО: НЕ добавляем префикс если он уже есть
            if (newsSlug.startsWith('News - ') || newsSlug.startsWith('Case - ')) {
                return newsSlug;
            }
            // Добавляем префикс только если его нет
            if (newsSlug.startsWith('case-')) {
                return `Case - ${newsSlug}`;
            }
            return `News - ${newsSlug}`;
        }
    }

    // 2. Проверяем data-page у body
    const bodyPage = document.body.getAttribute('data-page');
    if (bodyPage && bodyPage !== 'Home') {
        // Если уже содержит префикс, возвращаем как есть
        if (bodyPage.startsWith('News - ') || bodyPage.startsWith('Case - ')) {
            return bodyPage;
        }
        // Иначе добавляем префикс для новостных страниц
        if (bodyPage.startsWith('news-')) {
            return `News - ${bodyPage}`;
        }
        if (bodyPage.startsWith('case-')) {
            return `Case - ${bodyPage}`;
        }
        return bodyPage;
    }

    // 3. Fallback
    return 'Home';
}

// ИСПРАВЛЕННАЯ ФУНКЦИЯ: Загрузка атрибутов новости
async function loadNewsAttributes(blockElement, blockId, blockName) {
    try {
        console.log(`Loading news attributes for block: ${blockName} (ID: ${blockId})`);

        // Делаем запрос к API для получения новости по block_id
        const newsData = await apiGet(`${config.api.paths.newsByBlockId}/${blockId}`, {auth: false});

        if (newsData) {
            console.log(`News data found for block ${blockName}:`, newsData);

            // Устанавливаем атрибуты новости на блок
            blockElement.setAttribute('data-news-id', newsData.id);
            blockElement.setAttribute('data-news-status', newsData.status || 'draft');
            blockElement.setAttribute('data-news-slug', newsData.slug);

            // Обновляем ссылку если блок опубликован
            if (newsData.status === 'published' || blockId) {
                const newsLink = blockElement.querySelector('a.news-link');
                if (newsLink) {
                    const dynamicUrl = newsData.slug && newsData.slug.startsWith('case-') ? `/case/${newsData.slug}` : `/news/${newsData.slug}`;
                    newsLink.href = dynamicUrl;
                    newsLink.setAttribute('data-page', newsData.slug);
                    newsLink.classList.remove('inactive');
                    newsLink.classList.add('active');

                    console.log(`News link activated for ${blockName}: ${dynamicUrl}`);
                }
            }

            // НОВОЕ: Обновляем индикатор статуса только на news-feed
            if (shouldShowNewsStatusControls()) {
                updateNewsStatusIndicator(blockElement, newsData.status || 'draft');
            }

            // ИСПРАВЛЕНИЕ: Добавляем контролы статуса только на news-feed
            if (isAuthenticated() && shouldShowNewsStatusControls()) {
                // Небольшая задержка чтобы убедиться что newsManager загружен
                setTimeout(() => {
                    addNewsStatusControls(blockElement, newsData.id, newsData.status || 'draft');
                }, 100);
            }

        } else {
            console.log(`No news data found for block ${blockName}`);
        }

    } catch (error) {
        console.warn(`Failed to load news attributes for block ${blockName}:`, error);
        // Не прерываем выполнение, если новость не найдена
    }
}

// НОВАЯ ФУНКЦИЯ: Загрузка атрибутов кейса
async function loadCaseAttributes(blockElement, blockId, blockName) {
    try {
        console.log(`Loading case attributes for block: ${blockName} (ID: ${blockId})`);

        // Используем тот же API что и для новостей
        const caseData = await apiGet(`${config.api.paths.newsByBlockId}/${blockId}`, {auth: false});

        if (caseData) {
            console.log(`Case data found for block ${blockName}:`, caseData);

            // Устанавливаем атрибуты кейса на блок
            blockElement.setAttribute('data-news-id', caseData.id);
            blockElement.setAttribute('data-news-status', caseData.status || 'draft');
            blockElement.setAttribute('data-news-slug', caseData.slug);

            // Обновляем ссылку если кейс опубликован
            if (caseData.status === 'published' || blockId) {
                const caseLink = blockElement.querySelector('a.news-link-to-feed-container');
                if (caseLink) {
                    const dynamicUrl = `/case/${caseData.slug}`;
                    caseLink.href = dynamicUrl;
                    caseLink.setAttribute('data-page', caseData.slug);
                    caseLink.classList.remove('inactive');
                    caseLink.classList.add('active');

                    console.log(`Case link activated for ${blockName}: ${dynamicUrl}`);
                }
            }

            // Обновляем индикатор статуса только на cases.html
            if (shouldShowCaseStatusControls()) {
                updateCaseStatusIndicator(blockElement, caseData.status || 'draft');
            }

            // Добавляем контролы статуса только на cases.html
            if (isAuthenticated() && shouldShowCaseStatusControls()) {
                setTimeout(() => {
                    addCaseStatusControls(blockElement, caseData.id, caseData.status || 'draft');
                }, 100);
            }

        } else {
            console.log(`No case data found for block ${blockName}`);
        }

    } catch (error) {
        console.warn(`Failed to load case attributes for block ${blockName}:`, error);
    }
}

// НОВАЯ ФУНКЦИЯ: Обновление индикатора статуса для новостей
function updateNewsStatusIndicator(blockElement, status) {
    let indicator = blockElement.querySelector('.news-status-indicator');

    if (!indicator) {
        indicator = document.createElement('div');
        indicator.className = 'news-status-indicator';
        blockElement.appendChild(indicator);
    }

    indicator.className = `news-status-indicator status-${status}`;
    indicator.innerHTML = `<span class="status-text">${status.toUpperCase()}</span>`;
}

// НОВАЯ ФУНКЦИЯ: Обновление индикатора статуса для кейсов
function updateCaseStatusIndicator(blockElement, status) {
    let indicator = blockElement.querySelector('.news-status-indicator');

    if (!indicator) {
        indicator = document.createElement('div');
        indicator.className = 'news-status-indicator';
        blockElement.appendChild(indicator);
    }

    indicator.className = `news-status-indicator status-${status}`;
    indicator.innerHTML = `<span class="status-text">${status.toUpperCase()}</span>`;
}

// ИСПРАВЛЕННАЯ ФУНКЦИЯ: Добавление контролов статуса для новостей
function addNewsStatusControls(blockElement, newsId, currentStatus) {
    // Удаляем существующие контролы если есть
    const existingControls = blockElement.querySelector('.news-status-controls');
    if (existingControls) {
        existingControls.remove();
    }

    const controlsContainer = document.createElement('div');
    controlsContainer.className = 'news-status-controls';
    controlsContainer.innerHTML = `
    <div class="status-controls-inner">
      <span class="current-status">Status: ${currentStatus.toUpperCase()}</span>
      <button class="toggle-status-btn" data-news-id="${newsId}" data-current-status="${currentStatus}">
        ${currentStatus === 'draft' ? 'Publish' : 'Unpublish'}
      </button>
    </div>
  `;

    blockElement.appendChild(controlsContainer);

    // ИСПРАВЛЕНИЕ: Добавляем обработчик для переключения статуса
    const toggleBtn = controlsContainer.querySelector('.toggle-status-btn');
    toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // ВАЖНО: Используем глобальный newsManager вместо this
        if (window.newsManager) {
            console.log('Calling toggleNewsStatus with:', {newsId, currentStatus});
            window.newsManager.toggleNewsStatus(blockElement, newsId, currentStatus);
        } else {
            console.error('NewsManager not found in window.newsManager');
        }
    });

    console.log(`News status controls added for news ID: ${newsId}, status: ${currentStatus}`);
}

// НОВАЯ ФУНКЦИЯ: Добавление контролов статуса для кейсов
function addCaseStatusControls(blockElement, caseId, currentStatus) {
    const existingControls = blockElement.querySelector('.news-status-controls');
    if (existingControls) {
        existingControls.remove();
    }

    const controlsContainer = document.createElement('div');
    controlsContainer.className = 'news-status-controls';
    controlsContainer.innerHTML = `
    <div class="status-controls-inner">
      <span class="current-status">Status: ${currentStatus.toUpperCase()}</span>
      <button class="toggle-status-btn" data-case-id="${caseId}" data-current-status="${currentStatus}">
        ${currentStatus === 'draft' ? 'Publish' : 'Unpublish'}
      </button>
    </div>
  `;

    blockElement.appendChild(controlsContainer);

    const toggleBtn = controlsContainer.querySelector('.toggle-status-btn');
    toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (window.caseManager) {
            console.log('Calling toggleCaseStatus with:', {caseId, currentStatus});
            window.caseManager.toggleCaseStatus(blockElement, caseId, currentStatus);
        } else {
            console.error('CaseManager not found in window.caseManager');
        }
    });

    console.log(`Case status controls added for case ID: ${caseId}, status: ${currentStatus}`);
}

export function loadContentForBlocks(element) {
    // ВАЖНЫЙ ФИКС: Устанавливаем правильный page context перед загрузкой контента
    const pageContext = determinePageContext();
    console.log("page-context is", pageContext)

    const contentElements = element.querySelectorAll('[data-content]');

    // Если мы на странице новости/кейса и загружаем news-content блоки
    if (pageContext && pageContext !== 'Home') {
        const parentSection = element.closest('#news-content, #diagnostic-content') ||
                              element.querySelector('#news-content, #diagnostic-content');
        if (parentSection || element.id === 'news-content' || element.id === 'diagnostic-content') {
            contentElements.forEach(el => {
                // Устанавливаем правильный data-page только если он еще не установлен
                if (!el.getAttribute('data-page') || el.getAttribute('data-page') === 'Home') {
                    el.setAttribute('data-page', pageContext);
                    console.log(`Fixed data-page for ${el.getAttribute('data-content')} to: ${pageContext}`);
                }
            });
        }
    }

    loadContentForNewElements(contentElements);
}

export function initCarousel() {
    try {
        // Обновленная логика инициализации карусели
        window.carousel = window.carousel || {};
        window.carousel.initialized = false;

        // Используем обновленную функцию reinitializeCarousel,
        // которая будет искать секции с data-section="carousel"
        reinitializeCarousel();
    } catch (error) {
        console.error('[BlockSystem] Carousel initialization error:', error);
    }
}

export function findParentSection(element) {
    let parent = element;

    while (parent) {
        if (parent.hasAttribute && parent.hasAttribute('data-section')) {
            return parent;
        }
        parent = parent.parentElement;
    }

    return null;
}